import { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { FlightSearchSelectors } from "@b2bportal/core-flights";

/**
 * Gates a "add stay" action behind a child-ages dialog when the flight search
 * includes any non-adult passengers (children, lap infants, or seated infants).
 * Call `trigger()` instead of invoking the action directly; if ages are required,
 * it opens the dialog and defers the action until the user confirms.
 */
export const useChildAgesGate = (
  handleAddStay: ((childAges: number[]) => void) | undefined
) => {
  const childrenCount = useSelector(FlightSearchSelectors.getChildrenCount);
  const infantsOnLapCount = useSelector(
    FlightSearchSelectors.getInfantsOnLapCount
  );
  const infantsInSeatCount = useSelector(
    FlightSearchSelectors.getInfantsInSeatCount
  );
  const [isOpen, setIsOpen] = useState(false);

  const hasNonAdults =
    childrenCount + infantsOnLapCount + infantsInSeatCount > 0;

  const trigger = useCallback(() => {
    if (!handleAddStay) return;
    if (hasNonAdults) {
      setIsOpen(true);
    } else {
      handleAddStay([]);
    }
  }, [handleAddStay, hasNonAdults]);

  const onConfirm = useCallback(
    (childAges: number[]) => {
      setIsOpen(false);
      handleAddStay?.(childAges);
    },
    [handleAddStay]
  );

  const onCancel = useCallback(() => {
    setIsOpen(false);
  }, []);

  return {
    isOpen,
    childrenCount,
    infantsOnLapCount,
    infantsInSeatCount,
    trigger,
    onConfirm,
    onCancel,
  };
};
